import type { Views } from '@biopeak/db';
import { useQuery } from '@tanstack/react-query';
import { supabase } from './supabase.ts';

export type TriageRow = Views<'doctor_patient_triage'>;

/**
 * Ordem de severidade para a triagem: vermelho no topo (build-prompt §4.1).
 * Dentro do mesmo estado, quem está nele há mais tempo sobe — é quem espera
 * atenção há mais tempo, não necessariamente quem entrou por último.
 *
 * O gerador do Supabase marca toda coluna de VIEW como opcional (views não
 * carregam constraint NOT NULL), então `status` chega tipado como
 * `TrafficLightStatus | null` mesmo a view sempre preenchendo com `coalesce`.
 * `?? 'green'` aqui é o mesmo default que a própria view já aplica — não é
 * uma segunda decisão, é honrar em TypeScript o que o SQL já decidiu.
 */
const SEVERITY: Record<string, number> = { red: 0, yellow: 1, green: 2 };

export interface TriageCounts {
  red: number;
  yellow: number;
  green: number;
}

export function useTriage() {
  return useQuery({
    queryKey: ['doctor-triage'],
    queryFn: async (): Promise<TriageRow[]> => {
      // RLS + o filtro embutido na própria view (`doctor_patient_triage`)
      // restringem isso aos pacientes deste médico — não é um SELECT com
      // WHERE doctor_id aqui porque a view já resolve isso por dentro.
      const { data, error } = await supabase.from('doctor_patient_triage').select('*');
      if (error) throw error;

      return (data ?? []).sort((a, b) => {
        const severityDiff =
          (SEVERITY[a.status ?? 'green'] ?? 9) - (SEVERITY[b.status ?? 'green'] ?? 9);
        if (severityDiff !== 0) return severityDiff;
        const aTime = a.entered_state_at ? new Date(a.entered_state_at).getTime() : 0;
        const bTime = b.entered_state_at ? new Date(b.entered_state_at).getTime() : 0;
        return aTime - bTime;
      });
    },
  });
}

export function triageCounts(rows: TriageRow[]): TriageCounts {
  return rows.reduce(
    (acc, row) => {
      const status = row.status ?? 'green';
      if (status === 'red') acc.red += 1;
      else if (status === 'yellow') acc.yellow += 1;
      else acc.green += 1;
      return acc;
    },
    { red: 0, yellow: 0, green: 0 },
  );
}
